/**
 * Analytics Dashboard Page
 * Main entry point for the Analytics Dashboard System
 * Single-page layout with top bar selector
 */

import React, { useState, useEffect } from 'react';
import { Box, Container, Paper, Typography } from '@mui/material';
import { useAnalyticsDashboards, useCreateDashboard, useDeleteDashboard, useAddGraph } from '@/api/hooks/analytics/useAnalytics';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { DashboardSelector } from './components/DashboardSelector';
import { DashboardView } from './components/DashboardView';
import { DashboardCreator, DashboardSaveData } from './components/DashboardCreator';
import LoadingSpinner from '@/components/LoadingSpinner';
import { PRIVILEGES } from '@/api/types/dtos';
import { useHasPrivilege } from '@/components/authorization';

const AnalyticsDashboards: React.FC = () => {
  const {
    data: dashboards,
    isLoading,
    error,
    refetch,
  } = useAnalyticsDashboards();

  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPrivilege: canCreateDashboard } = useHasPrivilege(PRIVILEGES.CREATE_ANALYTICS);
  const { hasPrivilege: canDeleteDashboard } = useHasPrivilege(PRIVILEGES.DELETE_ANALYTICS);
  const { hasPrivilege: canEditDashboard } = useHasPrivilege(PRIVILEGES.UPDATE_ANALYTICS);

  const createDashboardMutation = useCreateDashboard();
  const deleteDashboardMutation = useDeleteDashboard();
  const addGraphMutation = useAddGraph();

  // Auto-select first dashboard on mount (alphabetically)
  useEffect(() => {
    if (!selectedDashboardId && dashboards && dashboards.length > 0) {
      const sortedDashboards = [...dashboards].sort((a, b) => a.name.localeCompare(b.name));
      setSelectedDashboardId(sortedDashboards[0].id);
    }
  }, [dashboards, selectedDashboardId]);

  const handleCreateDashboard = async (data: DashboardSaveData) => {
    // First create the dashboard
    const result = await createDashboardMutation.mutateAsync({
      name: data.name,
      description: data.description,
      scopeFilters: data.scopeFilters,
    });

    // Then add all graphs to the new dashboard
    if (result && data.graphs && data.graphs.length > 0) {
      const dashboardId = (result as any).id;
      for (const graph of data.graphs) {
        await addGraphMutation.mutateAsync({
          dashboardId,
          dto: graph,
        });
      }
    }

    queryClient.invalidateQueries({ queryKey: ['analytics'] });
    setIsCreating(false);
    refetch();
  };

  const handleDeleteDashboard = async (dashboardId: string) => {
    try {
      await deleteDashboardMutation.mutateAsync(dashboardId);
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      if (selectedDashboardId === dashboardId) {
        setSelectedDashboardId(null);
      }
      refetch();
      enqueueSnackbar('Dashboard eliminado', { variant: 'success' });
    } catch {
      enqueueSnackbar('Error al eliminar el dashboard', { variant: 'error' });
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <LoadingSpinner />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography color="error">Error loading dashboards: {(error as Error).message}</Typography>
        </Paper>
      </Container>
    );
  }

  // No dashboards exist
  if (!dashboards || dashboards.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <DashboardSelector
          dashboards={[]}
          selectedDashboardId={null}
          onSelectDashboard={() => {}}
          onCreateDashboard={() => setIsCreating(true)}
          canCreate={canCreateDashboard}
          isLoading={isLoading}
        />
        <Paper sx={{ p: 8, textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No dashboards yet
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Create your first dashboard to get started with analytics!
          </Typography>
        </Paper>

        <DashboardCreator
          open={isCreating}
          onClose={() => setIsCreating(false)}
          onSave={handleCreateDashboard}
          isLoading={createDashboardMutation.isPending}
        />
      </Container>
    );
  }

  // Main dashboard view
  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <DashboardSelector
        dashboards={dashboards}
        selectedDashboardId={selectedDashboardId}
        onSelectDashboard={setSelectedDashboardId}
        onCreateDashboard={() => setIsCreating(true)}
        canCreate={canCreateDashboard}
        isLoading={isLoading}
      />

      {selectedDashboardId && (
        <DashboardView
          dashboardId={selectedDashboardId}
          onBack={undefined} // No back button in single-page layout
          onDeleteDashboard={handleDeleteDashboard}
          canDelete={canDeleteDashboard}
          canEdit={canEditDashboard}
        />
      )}

      {/* Create Dashboard Modal */}
      <DashboardCreator
        open={isCreating}
        onClose={() => setIsCreating(false)}
        onSave={handleCreateDashboard}
        isLoading={createDashboardMutation.isPending}
      />
    </Container>
  );
};

export default AnalyticsDashboards;
